import { useTranslation } from 'react-i18next';

import { TranslationOptions } from '../types/i18n';

/**
 * Hook for feature-specific translations
 * Automatically prefixes translation keys with `features.{featureName}`
 *
 * @param feature - The feature name (e.g., 'dashboard', 'approvals', 'documents')
 * @returns Translation function with automatic prefix
 *
 * @example
 * const t = useFeatureTranslation('dashboard');
 * t('title') // Resolves to 'features.dashboard.title'
 * t('widgets.totalProjects') // Resolves to 'features.dashboard.widgets.totalProjects'
 */
export function useFeatureTranslation(feature: string) {
  const { t } = useTranslation();

  return (key: string, options?: TranslationOptions) => {
    const domainKey = `features.${feature}.${key}`;
    const domainResponse = t(domainKey, options);
    if (domainResponse !== domainKey) {
      return domainResponse;
    }
    // Fallback to default translation if feature-specific key is missing
    return t(key, options);
  };
}
